/**
 * World Map persistence helpers.
 *
 * World map data is stored in the user document under:
 *   users/{username}.orchestratorSavedData.worldMap
 *
 * The shape is:
 *   {
 *     layers: [{ id, name, imageUrl, z, visible, playerVisible }]
 *   }
 *
 * The session document also gets a `worldMap` field with only the
 * playerVisible layers so players can read it via the session token.
 */
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';

const USERS_COLLECTION = 'users';

function sanitize(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function getUserRef(db, username) {
  return doc(db, USERS_COLLECTION, username);
}

/** Load the world map for a user. Returns { layers: [] } or null. */
export async function loadWorldMap(db, username) {
  if (!username) return null;
  const snap = await getDoc(getUserRef(db, username));
  if (!snap.exists()) return null;
  const data = snap.data() || {};
  return data.orchestratorSavedData?.worldMap || null;
}

/** Save the full world map (all layers, including hidden ones) for a user. */
export async function saveWorldMap(db, username, worldMap) {
  if (!username) return;
  const payload = sanitize(worldMap);
  await updateDoc(getUserRef(db, username), {
    'orchestratorSavedData.worldMap': payload,
    'orchestratorSavedData.syncUpdatedAt': serverTimestamp(),
  });
}

/** Build the player-visible subset of world map layers. */
export function buildPlayerWorldMap(worldMap) {
  if (!worldMap) return null;
  const layers = (worldMap.layers || []).filter(
    (l) => l.visible !== false && l.playerVisible !== false
  );
  return { layers };
}
